import { z } from 'zod';
import { useUtilities } from './useUtilities';

export const useAiProfileValidation = () => {
  const { arrayHasValues, developerWarning, isObject } = useUtilities();

  const shouldShowValidationConsoleErrors = () => import.meta.env.DEV && Boolean(import.meta.env.VITE_SHOW_AI_PROFILE_SCHEMA_ERRORS);

  const validateBlobSchema = (contents: any) => {
    const schema = z.string().min(1);

    try {
      schema.parse(contents);
      return true;
    } catch (err) {
      developerWarning(shouldShowValidationConsoleErrors, err);
      return false;
    }
  };

  const validateListSchema = (contents: any, columns?: string[]) => {
    const stringSchema = z.string().array().nonempty();
    const objectSchema = z.object({}).passthrough().array().nonempty().superRefine((arr, ctx) => {
      if (!arrayHasValues(columns, { exactly: 1 })) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Columns must contain exactly one element when rendering list of objects',
        });
        return;
      }

      const [valueField] = columns;
      arr.forEach(item => {
        if (!isObject(item)) {
          throw new Error(`expected object, got ${typeof item}`);
        }
        if (!Object.hasOwn(item, valueField)) {
          throw new Error(`object does not have field ${JSON.stringify(arr)}`);
        }
      });
      if (!arr.every(item => isObject(item) && Object.hasOwn(item, valueField))) {

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid contents',
        });
      }
    });

    const schema = columns ? objectSchema : stringSchema;

    try {
      schema.parse(contents);
      return true;
    } catch (err) {
      developerWarning(shouldShowValidationConsoleErrors, err);
      return false;
    }
  };

  const validateTableSchema = (contents: any, columns: string[]) => {
    const columnSchema = z.string().min(1).array();

    const schema = z.object({}).passthrough().array().nonempty().superRefine((arr, ctx) => {
      if (!arr.every(row => columns.every(col => Object.hasOwn(row, col)))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Not all objects contain the provided columns',
        });
      }
    });

    try {
      schema.parse(contents);
      columnSchema.parse(columns);
      return true;
    } catch (err) {
      developerWarning(shouldShowValidationConsoleErrors, err);
      return false;
    }
  };

  const validateCompositeListSchema = (contents: any) => {
    if (!contents) return false;

    const keys = Object.keys(contents).filter(key => Object.hasOwn(contents, key));

    const schema = z.object(
      keys.reduce((acc, key) => {
        acc[key] = z.string().array();
        return acc;
      }, {} as Record<typeof keys[number], z.ZodTypeAny>),
    );

    try {
      schema.parse(contents);
      return true;
    } catch (err) {
      developerWarning(shouldShowValidationConsoleErrors, err);
      return false;
    }
  };

  return {
    validateBlobSchema,
    validateCompositeListSchema,
    validateListSchema,
    validateTableSchema,
  };
};
